import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '@/shared/lib/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Route error boundary caught an error', { error, errorInfo });
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[50vh] items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-foreground text-xl font-semibold">Something went wrong</h2>
            <p className="text-muted-foreground mt-2">This section encountered an error.</p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="bg-primary hover:bg-primary/90 text-primary-foreground mt-4 rounded-md px-4 py-2"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
